/**
 * Lista compacta de tareas relacionadas (subtareas y bloqueos en la ficha y en el diálogo de confirmación):
 * estado, ID y título en una sola línea (con «…»). Las hechas van tachadas (con `tacharCerradas`, también las
 * descartadas). Opcionalmente el ID enlaza a la tarea
 * (`?tarea=N`) y cada fila tiene un botón para quitar la relación.
 */
import type { MouseEvent } from 'react';
import { esEstadoCerrado, formatearNumero, type TareaRelacionada } from '@cdtec-backlog/contrato';
import { Icono } from '../../componentes/Icono';
import { EstadoTarea } from './MarcaCierre';
import './ListaRelacionadas.css';

interface Props {
  tareas: TareaRelacionada[];
  /** Nombre de la lista para lectores de pantalla. */
  etiqueta: string;
  /** Si viene, el ID es un enlace que abre esa tarea. */
  onAbrir?: (id: number) => void;
  /** Si viene y devuelve una etiqueta, la fila tiene un botón «quitar» con ese nombre accesible. */
  quitar?: (tarea: TareaRelacionada) => string | null;
  onQuitar?: (tarea: TareaRelacionada) => void;
  /** Deshabilita los botones de quitar (mientras se guarda). */
  ocupado?: boolean;
  /**
   * Tacha también las descartadas, no solo las hechas. En «Bloqueada por» una cerrada (hecha o descartada) ya no
   * bloquea; en las subtareas una descartada no cuenta como hecha, así que allí no se tacha.
   */
  tacharCerradas?: boolean;
}

/** Un clic normal abre la tarea en la misma página; con Ctrl/⌘ o la rueda, el navegador abre otra pestaña. */
export function clicEnlaceTarea(evento: MouseEvent, id: number, onAbrir: (id: number) => void) {
  if (evento.button !== 0 || evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.altKey)
    return;
  evento.preventDefault();
  onAbrir(id);
}

export function ListaRelacionadas({
  tareas,
  etiqueta,
  onAbrir,
  quitar,
  onQuitar,
  ocupado = false,
  tacharCerradas = false,
}: Props) {
  return (
    <ul className="relacionadas" aria-label={etiqueta}>
      {tareas.map((tarea) => {
        const id = formatearNumero(tarea.numero);
        const etiquetaQuitar = quitar?.(tarea) ?? null;
        const tachada =
          tarea.estado === 'HECHO' || (tacharCerradas && esEstadoCerrado(tarea.estado));
        return (
          <li key={tarea.id} className={tachada ? 'relacionadas__fila--hecha' : undefined}>
            <EstadoTarea estado={tarea.estado} />
            {onAbrir ? (
              <a
                className="relacionadas__id"
                href={`?tarea=${tarea.id}`}
                onClick={(evento) => clicEnlaceTarea(evento, tarea.id, onAbrir)}
              >
                {id}
              </a>
            ) : (
              <span className="relacionadas__id">{id}</span>
            )}
            <span className="relacionadas__titulo" title={tarea.titulo}>
              {tarea.titulo}
            </span>
            {etiquetaQuitar && onQuitar && (
              <button
                type="button"
                className="relacionadas__quitar"
                aria-label={etiquetaQuitar}
                title={etiquetaQuitar}
                disabled={ocupado}
                onClick={() => onQuitar(tarea)}
              >
                <Icono nombre="x" tamano={14} />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
